/**
 * The Fleet screen's pure model (M6 plan P28, spec §8): rows from the worker's `FleetView`, filters, sort, the
 * selection, bulk preset orders and the windowed list's cursor and window. Node-tested; the components only render
 * `FleetScreenState` and dispatch `FleetAction`s.
 *
 * - **Rows** (`fleetRows`): one per ship with the spec §8 columns: class, captain, order (and preset), location,
 *   status, cargo and earnings yesterday and today (the Corporation P&L ship line's basis, so the two screens show
 *   the same numbers; D-459 bug 6). Names are resolved once per view, never per filter change.
 * - **Filters** (`FleetFilter`): class, order, status and system; each cycles through `all` and the values present.
 * - **Sort** (`FleetSort`): any column, ascending or descending, ties by name then id.
 * - **Navigation** (`reduceFleet` with `nav`): the screen has a control bar (`BAR`) and the list. In the list, up
 *   and down move the cursor (the window follows), activate toggles the row's selection, right opens the row
 *   (the ship detail hook, P29) and left (or up from the first row) returns to the bar. In the bar, left and right
 *   move between controls, activate presses one and down enters the list. Back and start close.
 * - **Bulk presets** (`presetTargets`): the selected ships whose class may take the preset's order (orders.json
 *   `orders.<order>.classes`); the worker's `applyPreset` is all-or-nothing, so only eligible ships are sent and
 *   the rest are reported as skipped.
 *
 * Tuning (refresh, row height, window size, labels) lives in `fleetScreen.json`.
 */
import { z } from 'zod'
import { SLOT_IDS, type SlotId } from '../../data/m2Schemas'
import ordersJson from '../../data/strategic/orders.json'
import {
  ORDER_NAMES,
  SHIP_CLASS_NAMES,
  SHIP_STATUS_NAMES,
  type FleetShipView,
  type FleetView,
  type OrderName,
  type ShipClassName,
  type ShipStatusName,
} from '../../strategic/types'
import { UI_CONFIG } from '../config'
import { formatCredits } from '../format'
import { wareName } from '../strategic/names'
import type { NavInput } from '../strategic/mount'
import screenJson from './fleetScreen.json'
import type { LoadoutLine } from './ShipDetail'

// ---------------------------------------------------------------------------------------------
// Config and presets
// ---------------------------------------------------------------------------------------------

const label = z.string().min(1)

export const fleetScreenConfigSchema = z.object({
  comment: z.string().optional(),
  refreshS: z.number().positive(),
  list: z.object({
    rowHeight: z.number().positive(),
    visibleRows: z.number().int().positive(),
    overscan: z.number().int().nonnegative(),
    pageRows: z.number().int().positive(),
  }),
  classes: z.record(z.enum(SHIP_CLASS_NAMES), label),
  orders: z.record(z.enum(ORDER_NAMES), label),
  statuses: z.record(z.enum(SHIP_STATUS_NAMES), label),
})
export type FleetScreenConfig = z.infer<typeof fleetScreenConfigSchema>

export function loadFleetScreenConfig(raw: unknown = screenJson): FleetScreenConfig {
  return fleetScreenConfigSchema.parse(raw)
}

/** A preset as the Fleet screen offers it: its order and the ship classes that order allows. */
export interface PresetInfo {
  readonly id: string
  readonly name: string
  readonly order: OrderName
  readonly classes: readonly ShipClassName[]
}

const presetSourceSchema = z.object({
  orders: z.record(z.enum(ORDER_NAMES), z.object({ classes: z.array(z.enum(SHIP_CLASS_NAMES)) })),
  presets: z.array(z.object({ id: z.string().min(1), name: z.string().min(1), order: z.enum(ORDER_NAMES) })).min(1),
})

/** orders.json's presets in order, each with the classes its order allows. */
export function loadPresets(raw: unknown = ordersJson): readonly PresetInfo[] {
  const o = presetSourceSchema.parse(raw)
  return o.presets.map((p) => ({ id: p.id, name: p.name, order: p.order, classes: o.orders[p.order].classes }))
}

// ---------------------------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------------------------

export interface FleetRow {
  readonly id: string
  readonly name: string
  readonly hull: string
  readonly shipClass: ShipClassName
  readonly classLabel: string
  /** The captain's name, or null when the ship has none. */
  readonly captain: string | null
  readonly order: OrderName
  /** The order's display name ("Trade"). */
  readonly orderName: string
  /** "Trade", or "Trade · Safe hauler" when a preset set it. */
  readonly orderLabel: string
  readonly preset: string | null
  readonly status: ShipStatusName
  readonly statusLabel: string
  readonly systemId: string | null
  readonly systemName: string | null
  /** The station it is docked at, else its system, else "In transit". */
  readonly location: string
  readonly cargo: string
  readonly cargoQty: number
  readonly earnedPerDay: number
  readonly earnedToday: number
  readonly earnedYesterday: number
  readonly hp: number
  readonly insured: boolean
}

/** Display names for the ids a fleet view carries (unknown ids may return the id). */
export interface NameLookup {
  system(id: string): string
  /** A station's name; `systemId` is the ship's system (the station's, when docked). */
  station(id: string, systemId: string | null): string
}

/** Where ship `s` is, in words: its station (the core's name first), else its system, else "In transit". */
export function shipLocation(s: FleetShipView, names: NameLookup): string {
  if (s.stationId !== null) return s.stationName ?? names.station(s.stationId, s.systemId)
  return s.systemId === null ? 'In transit' : names.system(s.systemId)
}

export function fleetRow(s: FleetShipView, names: NameLookup, cfg: FleetScreenConfig, presets: readonly PresetInfo[]): FleetRow {
  const preset = s.preset === null ? null : (presets.find((p) => p.id === s.preset) ?? null)
  const orderName = cfg.orders[s.order.order]
  const systemName = s.systemId === null ? null : names.system(s.systemId)
  const location = shipLocation(s, names)
  return {
    id: s.id,
    name: s.name,
    hull: s.hull,
    shipClass: s.shipClass,
    classLabel: cfg.classes[s.shipClass],
    captain: s.captain?.name ?? null,
    order: s.order.order,
    orderName,
    orderLabel: preset === null ? orderName : `${orderName} · ${preset.name}`,
    preset: s.preset,
    status: s.status,
    statusLabel: cfg.statuses[s.status],
    systemId: s.systemId,
    systemName,
    location,
    cargo: s.cargo === null || s.cargo.qty <= 0 ? 'Empty' : `${Math.round(s.cargo.qty)} t ${wareName(s.cargo.ware)}`,
    cargoQty: s.cargo?.qty ?? 0,
    earnedPerDay: s.earnedPerDay,
    earnedToday: s.earnedToday,
    earnedYesterday: s.earnedYesterday,
    hp: s.hp,
    insured: s.insured,
  }
}

export function fleetRows(view: FleetView, names: NameLookup, cfg: FleetScreenConfig, presets: readonly PresetInfo[]): FleetRow[] {
  return view.ships.map((s) => fleetRow(s, names, cfg, presets))
}

/** "1,240 cr/day", signed (with a minus sign) when negative. */
export function earningsLabel(perDay: number): string {
  return `${perDay < 0 ? '−' : ''}${formatCredits(Math.abs(perDay))} cr/day`
}

/**
 * The ship detail's Loadout in slot order: each fitted slot's player label (ui.json `slots`, the Outfitting screen's)
 * and the module's name (modules.json; the id only when the module is unknown). D-459 bug 7.
 */
export function loadoutLines(
  fitted: Readonly<Partial<Record<SlotId, string>>>,
  moduleName: (id: string) => string | undefined,
  slotLabels: Readonly<Record<SlotId, string>> = UI_CONFIG.slots,
): LoadoutLine[] {
  const out: LoadoutLine[] = []
  for (const slot of SLOT_IDS) {
    const id = fitted[slot]
    if (id !== undefined) out.push({ slot: slotLabels[slot], module: moduleName(id) ?? id })
  }
  return out
}

/** "1,240 cr", whole credits as the P&L rounds them, signed (with a minus sign) when negative. */
export function creditsLabel(amount: number): string {
  const n = Math.round(amount)
  return `${n < 0 ? '−' : ''}${formatCredits(Math.abs(n))} cr`
}

// ---------------------------------------------------------------------------------------------
// Filters and sort
// ---------------------------------------------------------------------------------------------

export const FILTER_KEYS = ['shipClass', 'order', 'status', 'system'] as const
export type FilterKey = (typeof FILTER_KEYS)[number]
export const FILTER_LABELS: Readonly<Record<FilterKey, string>> = { shipClass: 'Class', order: 'Order', status: 'Status', system: 'System' }

/** Each key holds `all` or one value (a class, order, status or system id). */
export type FleetFilter = Readonly<Record<FilterKey, string>>
export const ALL = 'all'
export const NO_FILTER: FleetFilter = { shipClass: ALL, order: ALL, status: ALL, system: ALL }

export const SORT_KEYS = ['name', 'shipClass', 'captain', 'order', 'location', 'status', 'cargo', 'yesterday', 'today'] as const
export type SortKey = (typeof SORT_KEYS)[number]
export const SORT_LABELS: Readonly<Record<SortKey, string>> = {
  name: 'Ship',
  shipClass: 'Class',
  captain: 'Captain',
  order: 'Order',
  location: 'Location',
  status: 'Status',
  cargo: 'Cargo',
  yesterday: 'Yesterday',
  today: 'Today',
}

export interface FleetSort {
  readonly key: SortKey
  readonly desc: boolean
}
export const DEFAULT_SORT: FleetSort = { key: 'name', desc: false }

export interface FilterOption {
  readonly value: string
  readonly label: string
}

function filterValue(r: FleetRow, key: FilterKey): string | null {
  switch (key) {
    case 'shipClass':
      return r.shipClass
    case 'order':
      return r.order
    case 'status':
      return r.status
    case 'system':
      return r.systemId
  }
}

function filterValueLabel(r: FleetRow, key: FilterKey): string {
  switch (key) {
    case 'shipClass':
      return r.classLabel
    case 'order':
      return r.orderName
    case 'status':
      return r.statusLabel
    case 'system':
      return r.systemName ?? r.systemId ?? ''
  }
}

/** `all` then the values present in `rows` (by label), plus `current` when no row has it any more. */
export function filterOptions(rows: readonly FleetRow[], key: FilterKey, current: string = ALL): FilterOption[] {
  const seen = new Map<string, string>()
  for (const r of rows) {
    const v = filterValue(r, key)
    if (v !== null && !seen.has(v)) seen.set(v, filterValueLabel(r, key))
  }
  if (current !== ALL && !seen.has(current)) seen.set(current, current)
  const opts = [...seen].map(([value, l]) => ({ value, label: l })).sort((a, b) => a.label.localeCompare(b.label))
  return [{ value: ALL, label: 'All' }, ...opts]
}

export function matchesFilter(r: FleetRow, f: FleetFilter): boolean {
  for (const k of FILTER_KEYS) if (f[k] !== ALL && filterValue(r, k) !== f[k]) return false
  return true
}

function text(a: string | null, b: string | null): number {
  if (a === b) return 0
  if (a === null) return 1
  if (b === null) return -1
  return a.localeCompare(b)
}

function compareKey(a: FleetRow, b: FleetRow, key: SortKey): number {
  switch (key) {
    case 'name':
      return text(a.name, b.name)
    case 'shipClass':
      return text(a.classLabel, b.classLabel)
    case 'captain':
      return text(a.captain, b.captain)
    case 'order':
      return text(a.orderLabel, b.orderLabel)
    case 'location':
      return text(a.location, b.location)
    case 'status':
      return text(a.statusLabel, b.statusLabel)
    case 'cargo':
      return a.cargoQty - b.cargoQty
    case 'yesterday':
      return a.earnedYesterday - b.earnedYesterday
    case 'today':
      return a.earnedToday - b.earnedToday
  }
}

/** The rows `f` keeps, in `sort` order (ties by name then id, always ascending). A new array. */
export function visibleRows(rows: readonly FleetRow[], f: FleetFilter, sort: FleetSort): FleetRow[] {
  const out = rows.filter((r) => matchesFilter(r, f))
  const sign = sort.desc ? -1 : 1
  return out.sort((a, b) => sign * compareKey(a, b, sort.key) || text(a.name, b.name) || text(a.id, b.id))
}

// ---------------------------------------------------------------------------------------------
// Bulk presets
// ---------------------------------------------------------------------------------------------

export interface PresetTargets {
  /** Selected ships that can take the preset, in row order. */
  readonly eligible: readonly string[]
  /** Selected ships that cannot (class not allowed for the preset's order, or lost). */
  readonly skipped: readonly string[]
}

export function canTakePreset(r: FleetRow, p: PresetInfo): boolean {
  return r.status !== 'lost' && p.classes.includes(r.shipClass)
}

export function presetTargets(rows: readonly FleetRow[], selected: ReadonlySet<string>, p: PresetInfo): PresetTargets {
  const eligible: string[] = []
  const skipped: string[] = []
  for (const r of rows) {
    if (!selected.has(r.id)) continue
    if (canTakePreset(r, p)) eligible.push(r.id)
    else skipped.push(r.id)
  }
  return { eligible, skipped }
}

// ---------------------------------------------------------------------------------------------
// Window
// ---------------------------------------------------------------------------------------------

/** The window's first row after moving the cursor: unchanged while the cursor is inside it, clamped to the list. */
export function followCursor(first: number, cursor: number, visible: number, count: number): number {
  let f = first
  if (cursor < f) f = cursor
  else if (cursor >= f + visible) f = cursor - visible + 1
  return clampFirst(f, visible, count)
}

export function clampFirst(first: number, visible: number, count: number): number {
  return Math.max(0, Math.min(Math.floor(first), count - visible))
}

/** The rows to render: the window plus `overscan` on each side, as [start, end). */
export function windowSlice(first: number, visible: number, overscan: number, count: number): readonly [number, number] {
  const start = Math.max(0, first - overscan)
  const end = Math.min(count, first + visible + overscan)
  return [start, Math.max(start, end)]
}

// ---------------------------------------------------------------------------------------------
// Screen state and the reducer
// ---------------------------------------------------------------------------------------------

/** The control bar, left to right. */
export const BAR = ['filter:shipClass', 'filter:order', 'filter:status', 'filter:system', 'sort', 'sortDir', 'preset', 'apply', 'selectAll', 'clear'] as const
export type BarControl = (typeof BAR)[number]
export type FleetFocus = BarControl | 'list'

export interface FleetScreenState {
  readonly rows: readonly FleetRow[]
  readonly filter: FleetFilter
  readonly sort: FleetSort
  /** `rows` filtered and sorted (derived; rebuilt only when rows, filter or sort change). */
  readonly view: readonly FleetRow[]
  readonly selected: ReadonlySet<string>
  /** Index into `view`. */
  readonly cursor: number
  /** The window's first row. */
  readonly first: number
  readonly focus: FleetFocus
  /** The bar control the list returns to. */
  readonly barFocus: BarControl
  /** Index into the presets. */
  readonly preset: number
  readonly notice: string | null
}

export type FleetAction =
  | { type: 'rows'; rows: readonly FleetRow[] }
  | { type: 'nav'; input: NavInput }
  | { type: 'focus'; focus: FleetFocus }
  | { type: 'cycleFocus'; dir: 1 | -1 }
  | { type: 'press'; control: BarControl }
  | { type: 'cycleFilter'; key: FilterKey; dir: 1 | -1 }
  | { type: 'setFilter'; key: FilterKey; value: string }
  | { type: 'sortBy'; key: SortKey }
  | { type: 'cycleSort'; dir: 1 | -1 }
  | { type: 'toggleSortDir' }
  | { type: 'cyclePreset'; dir: 1 | -1 }
  | { type: 'toggle'; id: string }
  | { type: 'selectAll' }
  | { type: 'clear' }
  | { type: 'cursor'; index: number }
  | { type: 'page'; dir: 1 | -1 }
  | { type: 'home' }
  | { type: 'end' }
  | { type: 'scroll'; first: number }
  | { type: 'open'; id?: string }
  | { type: 'notice'; text: string | null }

/** What the host must do after an action: nothing, send a bulk preset, open a ship (P29) or close. */
export type FleetEffect =
  | { readonly kind: 'apply'; readonly preset: PresetInfo; readonly shipIds: readonly string[]; readonly skipped: number }
  | { readonly kind: 'open'; readonly id: string }
  | { readonly kind: 'close' }

export interface FleetStep {
  readonly state: FleetScreenState
  readonly effect: FleetEffect | null
}

export interface FleetModelContext {
  readonly presets: readonly PresetInfo[]
  readonly list: FleetScreenConfig['list']
}

export function createFleetScreenState(rows: readonly FleetRow[] = [], filter: FleetFilter = NO_FILTER, sort: FleetSort = DEFAULT_SORT): FleetScreenState {
  return {
    rows,
    filter,
    sort,
    view: visibleRows(rows, filter, sort),
    selected: new Set(),
    cursor: 0,
    first: 0,
    focus: 'list',
    barFocus: BAR[0],
    preset: 0,
    notice: null,
  }
}

function cycle(i: number, dir: 1 | -1, n: number): number {
  return n <= 0 ? 0 : (i + dir + n) % n
}

/** Rebuilds the view, keeping the cursor on the same ship when it is still shown. */
function withView(s: FleetScreenState, next: Pick<FleetScreenState, 'rows' | 'filter' | 'sort'>, ctx: FleetModelContext): FleetScreenState {
  const view = visibleRows(next.rows, next.filter, next.sort)
  const at = s.view[s.cursor]?.id
  const kept = at === undefined ? -1 : view.findIndex((r) => r.id === at)
  const cursor = kept >= 0 ? kept : Math.max(0, Math.min(s.cursor, view.length - 1))
  return { ...s, ...next, view, cursor, first: followCursor(s.first, cursor, ctx.list.visibleRows, view.length) }
}

function moveCursor(s: FleetScreenState, cursor: number, ctx: FleetModelContext): FleetScreenState {
  const c = Math.max(0, Math.min(cursor, s.view.length - 1))
  return { ...s, cursor: c, first: followCursor(s.first, c, ctx.list.visibleRows, s.view.length) }
}

function only(state: FleetScreenState): FleetStep {
  return { state, effect: null }
}

function applyStep(s: FleetScreenState, ctx: FleetModelContext): FleetStep {
  const preset = ctx.presets[s.preset]
  if (preset === undefined) return only({ ...s, notice: 'No preset to apply' })
  if (s.selected.size === 0) return only({ ...s, notice: 'Select ships first (activate a row, or Select all)' })
  const t = presetTargets(s.rows, s.selected, preset)
  if (t.eligible.length === 0) return only({ ...s, notice: `None of the selected ships can take ${preset.name}` })
  return { state: { ...s, notice: null }, effect: { kind: 'apply', preset, shipIds: t.eligible, skipped: t.skipped.length } }
}

function press(s: FleetScreenState, control: BarControl, ctx: FleetModelContext): FleetStep {
  const st = { ...s, focus: control, barFocus: control }
  switch (control) {
    case 'filter:shipClass':
      return reduceFleet(st, { type: 'cycleFilter', key: 'shipClass', dir: 1 }, ctx)
    case 'filter:order':
      return reduceFleet(st, { type: 'cycleFilter', key: 'order', dir: 1 }, ctx)
    case 'filter:status':
      return reduceFleet(st, { type: 'cycleFilter', key: 'status', dir: 1 }, ctx)
    case 'filter:system':
      return reduceFleet(st, { type: 'cycleFilter', key: 'system', dir: 1 }, ctx)
    case 'sort':
      return reduceFleet(st, { type: 'cycleSort', dir: 1 }, ctx)
    case 'sortDir':
      return reduceFleet(st, { type: 'toggleSortDir' }, ctx)
    case 'preset':
      return reduceFleet(st, { type: 'cyclePreset', dir: 1 }, ctx)
    case 'apply':
      return applyStep(st, ctx)
    case 'selectAll':
      return reduceFleet(st, { type: 'selectAll' }, ctx)
    case 'clear':
      return reduceFleet(st, { type: 'clear' }, ctx)
  }
}

function navStep(s: FleetScreenState, input: NavInput, ctx: FleetModelContext): FleetStep {
  if (input === 'back' || input === 'start') return { state: s, effect: { kind: 'close' } }
  if (s.focus === 'list') {
    switch (input) {
      case 'up':
        return s.cursor <= 0 ? only({ ...s, focus: s.barFocus }) : only(moveCursor(s, s.cursor - 1, ctx))
      case 'down':
        return only(moveCursor(s, s.cursor + 1, ctx))
      case 'left':
        return only({ ...s, focus: s.barFocus })
      case 'right':
        return reduceFleet(s, { type: 'open' }, ctx)
      case 'activate': {
        const r = s.view[s.cursor]
        return r === undefined ? only(s) : reduceFleet(s, { type: 'toggle', id: r.id }, ctx)
      }
    }
  }
  const i = BAR.indexOf(s.focus)
  switch (input) {
    case 'left':
    case 'right': {
      const next = BAR[cycle(i, input === 'left' ? -1 : 1, BAR.length)]
      return only({ ...s, focus: next, barFocus: next })
    }
    case 'down':
      return only({ ...s, focus: 'list', barFocus: s.focus })
    case 'up':
      return only(s)
    case 'activate':
      return press(s, s.focus, ctx)
  }
}

/** The Fleet screen's one state transition. Pure: returns the next state and what the host must do. */
export function reduceFleet(s: FleetScreenState, a: FleetAction, ctx: FleetModelContext): FleetStep {
  switch (a.type) {
    case 'rows': {
      const ids = new Set(a.rows.map((r) => r.id))
      const selected = new Set([...s.selected].filter((id) => ids.has(id)))
      return only({ ...withView(s, { rows: a.rows, filter: s.filter, sort: s.sort }, ctx), selected })
    }
    case 'nav':
      return navStep(s, a.input, ctx)
    case 'focus':
      return only(a.focus === 'list' ? { ...s, focus: 'list' } : { ...s, focus: a.focus, barFocus: a.focus })
    case 'cycleFocus': {
      const order: readonly FleetFocus[] = [...BAR, 'list']
      const next = order[cycle(order.indexOf(s.focus), a.dir, order.length)]
      return reduceFleet(s, { type: 'focus', focus: next }, ctx)
    }
    case 'press':
      return press(s, a.control, ctx)
    case 'cycleFilter': {
      const opts = filterOptions(s.rows, a.key, s.filter[a.key])
      const i = opts.findIndex((o) => o.value === s.filter[a.key])
      const value = opts[cycle(Math.max(0, i), a.dir, opts.length)].value
      return reduceFleet(s, { type: 'setFilter', key: a.key, value }, ctx)
    }
    case 'setFilter':
      return only(withView(s, { rows: s.rows, filter: { ...s.filter, [a.key]: a.value }, sort: s.sort }, ctx))
    case 'sortBy': {
      const sort = s.sort.key === a.key ? { key: a.key, desc: !s.sort.desc } : { key: a.key, desc: a.key === 'yesterday' || a.key === 'today' || a.key === 'cargo' }
      return only(withView(s, { rows: s.rows, filter: s.filter, sort }, ctx))
    }
    case 'cycleSort': {
      const key = SORT_KEYS[cycle(SORT_KEYS.indexOf(s.sort.key), a.dir, SORT_KEYS.length)]
      return only(withView(s, { rows: s.rows, filter: s.filter, sort: { key, desc: s.sort.desc } }, ctx))
    }
    case 'toggleSortDir':
      return only(withView(s, { rows: s.rows, filter: s.filter, sort: { key: s.sort.key, desc: !s.sort.desc } }, ctx))
    case 'cyclePreset':
      return only({ ...s, preset: cycle(s.preset, a.dir, ctx.presets.length) })
    case 'toggle': {
      const selected = new Set(s.selected)
      if (!selected.delete(a.id)) selected.add(a.id)
      return only({ ...s, selected })
    }
    case 'selectAll':
      return only({ ...s, selected: new Set([...s.selected, ...s.view.map((r) => r.id)]) })
    case 'clear':
      return only({ ...s, selected: new Set() })
    case 'cursor':
      return only({ ...moveCursor(s, a.index, ctx), focus: 'list' })
    case 'page':
      return only(moveCursor(s, s.cursor + a.dir * ctx.list.pageRows, ctx))
    case 'home':
      return only(moveCursor(s, 0, ctx))
    case 'end':
      return only(moveCursor(s, s.view.length - 1, ctx))
    case 'scroll':
      return only({ ...s, first: clampFirst(a.first, ctx.list.visibleRows, s.view.length) })
    case 'open': {
      const id = a.id ?? s.view[s.cursor]?.id
      return id === undefined ? only(s) : { state: s, effect: { kind: 'open', id } }
    }
    case 'notice':
      return only({ ...s, notice: a.text })
  }
}

/** The notice after a bulk preset: "Belt miner set on 3 ships (1 skipped: class)". */
export function appliedNotice(preset: PresetInfo, sent: number, skipped: number): string {
  const ships = `${sent} ship${sent === 1 ? '' : 's'}`
  return skipped > 0 ? `${preset.name} set on ${ships} (${skipped} skipped: their class cannot take ${preset.name})` : `${preset.name} set on ${ships}`
}

/** The label of a filter's current value ("All", "Miner", the system's name). */
export function filterValueText(rows: readonly FleetRow[], key: FilterKey, value: string): string {
  return filterOptions(rows, key, value).find((o) => o.value === value)?.label ?? value
}
